/*
 * Purpose: L1 outcome interpretation — Super Intent before full plan
 * Last modified: 2026-06-27 | Agent
 * Completeness: 90
 */
import { hasResolvedSecret, resolveSecret } from '@/lib/resolveSecret';
import { readEnvOr } from '@/lib/runtimeEnv';
import { AVERAGE_USER_TEMPLATES } from '@/services/averageUserTemplates';
import {
  intentInterpretationSchema,
  type IntentInterpretationPayload,
} from './intentInterpretSchema';
import type { ComposeMode } from './missionTypes';

const MODEL = readEnvOr('VITE_OPENAI_MODEL', 'gpt-4o-mini');

export interface IntentInterpretation extends IntentInterpretationPayload {
  rawOutcome: string;
  plannerSource: 'llm' | 'rules';
  suggestedComposeMode: ComposeMode;
}

const TEMPLATE_HINTS = Object.keys(AVERAGE_USER_TEMPLATES);

function ruleBasedInterpret(rawOutcome: string): IntentInterpretation {
  const lower = rawOutcome.toLowerCase();
  const needsGmail = /\b(email|gmail|inbox|receipt|job alert|linkedin.*email)\b/i.test(lower);
  const needsLinkedIn = /\blinkedin\b/i.test(lower);
  const needsOpenAi = /\b(resume|cover letter|tailor|ai|gpt)\b/i.test(lower);
  const isFollowUp = /\b(follow[\s-]?up|remind|over time|days? later)\b/i.test(lower);
  const isComplex =
    needsGmail && needsLinkedIn && needsOpenAi || lower.split(/\s+/).length > 35;

  let matchedTemplateHint: string | undefined;
  for (const key of TEMPLATE_HINTS) {
    const label = key.replace(/_/g, ' ');
    if (lower.includes(label.split(' ')[0])) {
      matchedTemplateHint = key;
      break;
    }
  }
  if (needsLinkedIn && lower.includes('job')) matchedTemplateHint = 'linkedin_job_hunt_pipeline';

  const suggestedComposeMode: ComposeMode =
    isComplex || isFollowUp || !matchedTemplateHint ? 'guided' : 'express';

  const gaps: string[] = [];
  if (needsOpenAi && !hasResolvedSecret('OPENAI_API_KEY')) {
    gaps.push('OpenAI API key not configured — Guided mode recommended');
  }
  if (needsGmail) gaps.push('Gmail or inbound email path may be required');
  if (needsLinkedIn) gaps.push('LinkedIn connector may be required');

  return {
    rawOutcome,
    interpretedGoal: rawOutcome.length > 120 ? `${rawOutcome.slice(0, 117)}…` : rawOutcome,
    confidence: matchedTemplateHint ? 0.82 : 0.62,
    suggestedComposeMode,
    assumptions: [
      'AutoBlocks will assemble blocks — you confirm before anything runs live.',
    ],
    gaps,
    needsGmail,
    needsLinkedIn,
    needsOpenAi,
    matchedTemplateHint,
    plannerSource: 'rules',
  };
}

async function llmInterpret(rawOutcome: string): Promise<IntentInterpretation | null> {
  const apiKey = resolveSecret('OPENAI_API_KEY');
  if (!apiKey) return null;

  const templates = TEMPLATE_HINTS.join(', ');
  const system = `You interpret user OUTCOMES for AutoBlocks (workflow automation). Return JSON only.
Fields: interpretedGoal (plain English), confidence 0-1, suggestedComposeMode "express"|"guided",
assumptions[], gaps[], needsGmail?, needsLinkedIn?, needsOpenAi?, matchedTemplateHint? (one of: ${templates}).
Use express when outcome maps cleanly to a template and setup seems straightforward; guided otherwise.`;

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: MODEL,
        temperature: 0.2,
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: system },
          { role: 'user', content: `OUTCOME:\n${rawOutcome}` },
        ],
      }),
    });

    if (!response.ok) return null;
    const data = await response.json();
    const content = data?.choices?.[0]?.message?.content;
    if (!content) return null;

    const parsed = intentInterpretationSchema.parse(JSON.parse(content));
    return {
      ...parsed,
      rawOutcome,
      suggestedComposeMode: parsed.suggestedComposeMode ?? 'guided',
      plannerSource: 'llm',
    };
  } catch {
    return null;
  }
}

export async function interpretOutcomeAsync(rawOutcome: string): Promise<IntentInterpretation> {
  const trimmed = rawOutcome.trim();
  if (!trimmed) {
    throw new Error('Enter an outcome to interpret');
  }

  const fromLlm = await llmInterpret(trimmed);
  if (fromLlm) return fromLlm;
  return ruleBasedInterpret(trimmed);
}
